import { HttpClient } from '@angular/common/http';
import { Injectable, signal, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AccountInfo, AuthenticationResult, PublicClientApplication } from '@azure/msal-browser';
import { defer, Observable, throwError } from 'rxjs';
import { catchError, finalize, shareReplay, switchMap, tap } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { ApiResponse, AuthConfig, LoginData, UserInfo } from '../types/auth.types';

/**
 * Servicio que gestiona la autenticación con Azure AD (MSAL) y el consumo de la API
 * para manejar tokens JWT (Access y Refresh).
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private router = inject(Router);
  private http = inject(HttpClient);

  /**
   * Clave de almacenamiento local para la información del usuario
   */
  private readonly userInfoKey = 'userInfo';

  /**
   * Clave de almacenamiento local para el Access Token
   */
  private readonly accessTokenKey = 'jwtToken';

  /**
   * Clave de almacenamiento local para el Refresh Token
   */
  private readonly refreshTokenKey = 'jwtRefreshToken';

  /**
   * Configuración específica para MSAL (Azure AD)
   */
  private config: AuthConfig = {
    clientId: environment.azure.clientId,
    authority: `https://login.microsoftonline.com/${environment.azure.tenantId}`,
    redirectUri: environment.azure.redirectUri,
    scopes: environment.azure.scopes,
  };

  private readonly apiAuthUrl = environment.apiAuthUrl;
  private readonly apiUrl = environment.apiUrl;
  private readonly systemId = environment.systemId;

  /**
   * Instancia principal de la librería MSAL
   */
  private msalInstance: PublicClientApplication;

  /**
   * Bandera que indica si MSAL ya ha sido inicializado
   */
  private initialized = false;

  private refreshInFlight$: Observable<string> | null = null;

  /**
   * Signal que contiene la información de la cuenta autenticada en MSAL
   */
  public user = signal<AccountInfo | null>(null);

  /**
   * Signal que contiene los datos principales del usuario (nombre, email, roles)
   * provenientes de nuestra API/Backend
   */
  public userInfo = signal<UserInfo | null>(null);

  /**
   * Signal que contiene la lista de roles del usuario
   */
  private userRoles = signal<string[]>([]);

  /**
   * Signal que contiene el mensaje de error de autenticación
   */
  public authError = signal<string | null>(null);

  /**
   * Signal que contiene el indicador de iniciando sesión
   */
  public isSigningIn = signal<boolean>(false);

  /**
   * Constructor del AuthService
   * @param router Enrutador de Angular para redireccionar según estados de auth
   * @param http Cliente HTTP para comunicarse con la API
   */
  constructor() {
    this.msalInstance = new PublicClientApplication({
      auth: {
        clientId: this.config.clientId,
        authority: this.config.authority,
        redirectUri: this.config.redirectUri,
        navigateToLoginRequestUrl: false,
        // postLogoutRedirectUri: 'https://tusitio.com/auth/logout', // opcional
      },
      cache: {
        cacheLocation: 'sessionStorage',
        storeAuthStateInCookie: true,
        secureCookies: true,
      },
      system: {
        allowRedirectInIframe: true,
        windowHashTimeout: 60000,
        iframeHashTimeout: 6000,
        navigateFrameWait: 0,
      },
    });

    this.msalInstance.initialize().then(() => {
      this.initialized = true;
      this.initializeAuth();
    });
  }

  /**
   * Realiza las tareas iniciales de autenticación:
   * - Maneja la promesa de redirección de MSAL
   * - Verifica si ya hay cuentas en sesión
   * - Redirecciona a /auth/login si el usuario no está autenticado
   * @private
   */
  private initializeAuth(): void {
    defer(() => this.msalInstance.handleRedirectPromise())
      .pipe(
        tap((result) => {
          if (result) {
            this.handleAuthenticationSuccess(result);
          } else {
            const accounts = this.msalInstance.getAllAccounts();
            if (accounts.length > 0) {
              this.user.set(accounts[0]);
              this.tapUserInfo();
              if (this.router.url.includes('/auth/login')) {
                this.router.navigate(['/app/home']);
              }
            } else if (this.isSimulationMode()) {
              this.handleSimulation();
            } else {
              this.clearSession();
              this.router.navigate(['/auth/login']);
            }
          }
        }),
        catchError((error) => {
          console.error('[AUTH] handleRedirectPromise error:', error);
          if (this.isSimulationMode()) {
            this.handleSimulation();
          } else {
            this.router.navigate(['/auth/login']);
          }
          return defer(async () => null);
        }),
      )
      .subscribe();
  }

  /**
   * Dispara el proceso de inicio de sesión usando redirección de Azure AD (MSAL).
   * @returns Observable<void>
   */
  public login(): Observable<void> {
    if (!this.initialized) {
      return throwError(() => new Error('MSAL no se ha inicializado aún'));
    }

    return defer(async () => {
      try {
        await this.msalInstance.loginRedirect({ scopes: this.config.scopes });
      } catch (error: unknown) {
        const msalError = error as { errorCode?: string };
        if (msalError?.errorCode === 'interaction_in_progress') {
          // Estado de interacción colgado — limpiar y reintentar
          this.clearMsalInteraction();
          await this.msalInstance.loginRedirect({ scopes: this.config.scopes });
        } else {
          throw error;
        }
      }
    });
  }

  private clearMsalInteraction(): void {
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith('msal.'))
      .forEach((k) => sessionStorage.removeItem(k));
  }

  /**
   * Limpia el estado de sesión local sin disparar ninguna interacción MSAL.
   * Usar cuando un error de red/auth requiere redirigir a login de forma silenciosa.
   */
  public clearSession(): void {
    this.user.set(null);
    this.userInfo.set(null);
    this.userRoles.set([]);
    localStorage.removeItem(this.userInfoKey);
    localStorage.removeItem(this.accessTokenKey);
    localStorage.removeItem(this.refreshTokenKey);
  }

  /**
   * Cierra la sesión del usuario tanto en nuestra API como en Azure AD con redirección
   * @returns Observable<void>
   */
  public logout(): Observable<void> {
    const info = this.getUserInfo();
    const userId = info?.userId ?? '';

    return this.http
      .post<ApiResponse<unknown>>(`${this.apiAuthUrl}/auth/logout`, {
        userId,
        systemId: this.systemId,
      })
      .pipe(
        tap({
          next: (resp) => {
            if (!resp?.isSuccess) {
              console.warn(`Logout backend no exitoso: ${resp?.message}`);
            }
            this.clearSession();
          },
        }),
        switchMap(() =>
          defer(() => {
            this.msalInstance.logoutRedirect();
            return Promise.resolve();
          }),
        ),
        catchError((error) => {
          console.error('Error durante el logout:', error);
          this.clearSession();
          this.router.navigate(['/auth/login']);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Indica si hay un usuario autenticado en MSAL (cuentas en memoria)
   * @returns true si el usuario está autenticado, false en caso contrario
   */
  public isAuthenticated(): boolean {
    const hasUserInfo = this.getUserInfo() !== null;
    const hasMsalAccounts = this.msalInstance.getAllAccounts().length > 0;
    return hasMsalAccounts && hasUserInfo;
  }

  /**
   * Devuelve la información de usuario guardada en localStorage
   * @returns UserInfo | null - Objeto con la información del usuario o null si no existe o es inválido
   */
  public getUserInfo(): UserInfo | null {
    try {
      const storedData = localStorage.getItem(this.userInfoKey);

      if (!storedData) {
        if (this.isSimulationMode()) {
          return this.getSimulatedUserInfo();
        }
        return null;
      }

      const parsedData = JSON.parse(storedData);

      // Validación básica para asegurar que el objeto cumple con la interfaz UserInfo
      if (typeof parsedData !== 'object' || parsedData === null) {
        if (this.isSimulationMode()) {
          return this.getSimulatedUserInfo();
        }
        return null;
      }

      return parsedData as UserInfo;
    } catch (error) {
      console.error('Error al recuperar información de usuario:', error);
      if (this.isSimulationMode()) {
        return this.getSimulatedUserInfo();
      }
      return null;
    }
  }

  /**
   * Guarda un objeto con la información del usuario en localStorage
   * @param userInfo Objeto con información del usuario (ej. nombre, email, roles)
   */
  public setUserInfo(userInfo: unknown): void {
    localStorage.setItem(this.userInfoKey, JSON.stringify(userInfo));
  }

  /**
   * Devuelve el Access Token (JWT) almacenado en localStorage
   * @returns string | null
   */
  public getAccessToken(): string | null {
    const token = localStorage.getItem(this.accessTokenKey);
    if (!token && this.isSimulationMode()) {
      return environment.token ?? null;
    }
    return token;
  }

  /**
   * Guarda el Access Token (JWT) en localStorage
   * @param token Cadena JWT
   */
  public setAccessToken(token: string): void {
    localStorage.setItem(this.accessTokenKey, token);
  }

  /**
   * Devuelve el Refresh Token almacenado
   * @returns string | null
   */
  public getRefreshToken(): string | null {
    return localStorage.getItem(this.refreshTokenKey);
  }

  /**
   * Guarda el Refresh Token en localStorage
   * @param token Cadena del Refresh Token
   */
  public setRefreshToken(token: string): void {
    localStorage.setItem(this.refreshTokenKey, token);
  }

  /**
   * Invoca el endpoint de nuestra API para refrescar el token usando el Refresh Token
   * @param expiredToken JWT expirado
   * @returns Observable<string> con el nuevo Access Token
   */
  public handleRefreshToken(expiredToken: string): Observable<string> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return throwError(() => new Error('No existe Refresh Token almacenado'));
    }

    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    const request$ = this.http
      .post<ApiResponse<LoginData>>(`${this.apiAuthUrl}/auth/refresh`, {
        accessToken: expiredToken || '',
        refreshToken,
      })
      .pipe(
        switchMap((response) => {
          if (!response?.isSuccess || !response?.data) {
            return throwError(() => new Error(response?.message || 'Refresh token inválido'));
          }
          this.setAccessToken(response.data.token);
          this.setRefreshToken(response.data.refreshToken);
          return defer(() => Promise.resolve(response.data.token));
        }),
        finalize(() => {
          this.refreshInFlight$ = null;
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
      );

    this.refreshInFlight$ = request$;
    return request$;
  }

  /**
   * Determina si un token está por expirar pronto (por ejemplo, en 30s)
   * @param token JWT a evaluar
   * @returns true si faltan <= 30s para expirar, false de lo contrario
   */
  public isTokenExpiringSoon(token: string): boolean {
    if (this.isSimulationMode() && token === environment.token) {
      return false; // Simulated tokens never expire
    }
    try {
      // Dividimos el token por '.', el segundo índice (payload) está en Base64
      const payload = token.split('.')[1];
      if (!payload) return true;

      // Decodificamos Base64
      const decoded = JSON.parse(atob(payload));
      const exp = decoded.exp; // exp es tiempo UNIX en segundos

      if (!exp) return true;

      const nowInSec = Math.floor(Date.now() / 1000);
      const secondsLeft = exp - nowInSec;
      const REFRESH_MARGIN = 30; // margen de 30 segundos

      return secondsLeft <= REFRESH_MARGIN;
    } catch {
      return true;
    }
  }

  /**
   * Maneja el proceso posterior a una autenticación exitosa con MSAL.
   * - Establece la cuenta en la Signal 'user'
   * - Adquiere el token de Azure en modo silencioso
   * - Envía el token de Azure a nuestra API para obtener un JWT propio
   * - Decodifica y almacena roles, email y nombre de usuario
   * @param result Objeto con datos del resultado de la autenticación
   * @private
   */
  private handleAuthenticationSuccess(result: AuthenticationResult): void {
    if (!result.account) return;

    this.user.set(result.account);

    const idTokenClaims = result.idTokenClaims as Record<string, unknown>;
    const name = String(idTokenClaims['name'] || idTokenClaims['given_name'] || '');
    const emails = idTokenClaims['emails'] as string[] | undefined;
    const email = String(idTokenClaims['preferred_username'] || emails?.[0] || '');

    defer(() =>
      this.msalInstance.acquireTokenSilent({
        scopes: this.config.scopes,
        account: result.account,
      }),
    )
      .pipe(
        switchMap((tokenResponse) => {
          this.authError.set(null);
          this.isSigningIn.set(true);

          return this.http
            .post<ApiResponse<LoginData>>(`${this.apiAuthUrl}/auth/login`, {
              tipo: 'Ms365',
              token: tokenResponse.accessToken,
              idSistema: this.systemId,
            })
            .pipe(
              tap((response) => {
                if (!response.isSuccess || !response.data) {
                  const msg = response.message || 'Error al validar credenciales';
                  this.authError.set(msg);
                  this.user.set(null);
                  this.userInfo.set(null);
                  this.userRoles.set([]);
                  this.router.navigate(['/auth/login']);
                  this.isSigningIn.set(false);
                  throw new Error(msg);
                }

                const { token, refreshToken, profile } = response.data;
                this.setAccessToken(token);
                this.setRefreshToken(refreshToken);

                let roles: string[] = Array.isArray(profile?.roles)
                  ? (profile!.roles as string[])
                  : [];
                if (roles.length === 0) {
                  try {
                    const jwt = JSON.parse(atob(token.split('.')[1])) as Record<string, unknown>;
                    const raw =
                      jwt['roles'] ??
                      jwt['role'] ??
                      jwt['Roles'] ??
                      jwt['Role'] ??
                      jwt['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
                    roles = Array.isArray(raw) ? (raw as string[]) : raw ? [String(raw)] : [];
                  } catch {
                    /* ignore */
                  }
                }
                this.userRoles.set(roles);

                const userInfo: UserInfo = {
                  userId: profile?.idUsuario !== undefined ? String(profile.idUsuario) : undefined,
                  name: profile?.nombreUsuario || name,
                  email: profile?.email || email,
                  employeeNumber: profile?.numeroEmpleado
                    ? String(profile.numeroEmpleado)
                    : undefined,
                  roles,
                };

                this.setUserInfo(userInfo);
                this.tapUserInfo();
                this.isSigningIn.set(false);
                this.router.navigate(['/app/home']);

                const bypass = environment.development?.bypassOrganoFilter ?? false;
                if (!bypass && !roles.includes('Usuarios.Administrador')) {
                  this.http
                    .get<
                      ApiResponse<{
                        idOrgano: number;
                        nombreOrgano: string;
                      } | null>
                    >(`${this.apiUrl}/AdministracionUsuarios/mi-organo`)
                    .subscribe({
                      next: (res) => {
                        if (res.isSuccess && res.data) {
                          const updated = {
                            ...userInfo,
                            organoId: res.data.idOrgano,
                            organoName: res.data.nombreOrgano,
                          };
                          this.setUserInfo(updated);
                          this.tapUserInfo();
                        }
                      },
                    });
                }
              }),
              catchError((httpError) => {
                console.error('[AUTH] /auth/login error:', httpError?.status, httpError?.error);
                const errorMessage = 'Error al validar token durante el inicio de sesión';
                this.authError.set(errorMessage);
                this.user.set(null);
                this.userInfo.set(null);
                this.userRoles.set([]);
                this.router.navigate(['/auth/login']);
                this.isSigningIn.set(false);
                return throwError(() => new Error(errorMessage));
              }),
            );
        }),
        catchError((error) => {
          console.error(
            '[AUTH] acquireTokenSilent error:',
            (error as { errorCode?: string })?.errorCode,
          );
          this.userRoles.set([]);
          return defer(async () => null);
        }),
      )
      .subscribe();
  }

  private tapUserInfo(): void {
    const userInfo = this.getUserInfo();
    if (userInfo) {
      this.userInfo.set(userInfo);
      this.userRoles.set(userInfo.roles ?? []);
    }
  }

  /**
   * Verifica si el usuario posee un rol específico
   * @param role Rol a verificar (ej. 'Admin')
   * @returns true si el usuario tiene ese rol, false de lo contrario
   */
  public hasRole(role: string): boolean {
    const roles = this.userRoles();
    return roles.includes(role) || roles.includes(role.toLowerCase());
  }

  /**
   * Verifica si estamos en modo simulación
   * @private
   */
  private isSimulationMode(): boolean {
    return !!(environment.development?.useMockData && environment.token);
  }

  /**
   * Maneja el inicio de sesión simulado
   * @private
   */
  private handleSimulation(): void {
    const userInfo = this.getSimulatedUserInfo();
    if (!userInfo) return;

    this.userInfo.set(userInfo);
    this.userRoles.set(userInfo.roles);

    // If on login page, redirect to home
    if (this.router.url.includes('/auth/login')) {
      this.router.navigate(['/app/home']);
    }
  }

  /**
   * Obtiene la información del usuario simulado a partir del token en environment
   * @private
   * @returns UserInfo | null
   */
  private getSimulatedUserInfo(): UserInfo | null {
    const token = environment.token;
    if (!token) return null;

    try {
      // Parse simulation string: SIMULATE numero_empleado:12345;id_usuario:67890;roles=admin,group_admin;name=Lucia;id_sistema=25
      const parts = token.replace('SIMULATE ', '').split(';');
      const data: Record<string, string> = {};

      parts.forEach((part) => {
        const separator = part.includes('=') ? '=' : ':';
        const [key, value] = part.split(separator);
        data[key.trim()] = value?.trim();
      });

      const roles = data['roles'] ? data['roles'].split(',') : [];

      return {
        name: data['name'] || 'Simulated User',
        email: data['email'] || 'simulated@example.com',
        roles: roles,
        employeeNumber: data['numero_empleado'],
        userId: data['id_usuario'],
      };
    } catch (e) {
      console.error('Error al parsear token de simulación:', e);
      return null;
    }
  }
}
